import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Collapse,
  Alert,
  TextField,
  InputAdornment,
  Container,
  Grid,
  Stack,
  Paper,
  Checkbox,
  FormControlLabel,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Skeleton,
  LinearProgress,
  //  For the laboratory panels in the patient dialog — a result set is a table,
  //  and a list of Typography rows pretending to be one never lines up.
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Fade,
  Backdrop,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search,
  Person,
  CalendarToday,
  Warning,
  ExpandMore,
  ExpandLess,
  AccessTime,
  LocationOn,
  LocalHospital,
  PersonOutline,
  MonitorHeart,
  NoteAlt,
  //  The laboratory section's heading icon.
  Science as ScienceIcon,
  Close,
  Favorite,
  Thermostat,
  Scale,
  Height,
  BloodtypeOutlined,
  Refresh,
  Visibility,
  Print
} from '@mui/icons-material';
import { loadPharmacyclinicalNotes, loadPharmacyPatients, loadPharmacyPatientsMed, loadPharmacyVitals,
  prescriptionServeAll, updateMedicationStatutt ,notServedMedicationUrl,
  pharmacyAttentionOpen, loadPharmacyPatientHistory } from '../BaseURL';
import PharmacyAttentionDialog from './PharmacyAttentionDialog';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Clear } from '@mui/icons-material';
import WcIcon from '@mui/icons-material/Wc';
import { successMessage } from '../Message';
import MedicationHistory from './MedicationHistory';
import MedicationLiquidIcon from '@mui/icons-material/MedicationLiquid';
import RestockNotificationDialog from './RestockNotificationDialog';
import ReversalDialog from '../ReversalDialog';

// ── Theme tokens (mirrors Pharmacy/Procurement Home.js) ───────────────────
const PRIMARY      = '#41a386';
const PRIMARY_DARK = '#2f8068';
const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
const BORDER       = '#e5e9ef';
const MUTED        = '#64748b';
const STRONG       = '#0f172a';
const LABEL_SX = {
  fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
  textTransform: 'uppercase', letterSpacing: '0.6px',
};

// Status palette — single source of truth for the medication state pills.
const STATUS_COLORS = {
  served:     { tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'Served'     },
  completed:  { tint: 'rgba(16,185,129,0.10)', strong: '#15803D', label: 'Completed'  },
  pending:    { tint: 'rgba(217,119,6,0.10)',  strong: '#92400E', label: 'Pending'    },
  not_served: { tint: 'rgba(220,38,38,0.10)',  strong: '#991B1B', label: 'Not served' },
  unknown:    { tint: '#f1f5f9',                strong: '#475569', label: 'Unknown'    },
};
const StatusPill = ({ status }) => {
  const key = String(status || '').toLowerCase().replace(/\s+/g, '_');
  const s = STATUS_COLORS[key] || STATUS_COLORS.pending;
  return (
    <Box sx={{
      display: 'inline-flex', alignItems: 'center',
      px: 1, py: 0.25, borderRadius: 1,
      bgcolor: s.tint, color: s.strong,
      fontSize: '0.7rem', fontWeight: 700,
      textTransform: 'uppercase', letterSpacing: '0.4px',
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </Box>
  );
};

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Loading Skeleton Component for Prescription Cards
const PrescriptionCardSkeleton = () => (
  <Card elevation={2} sx={{ width: '100%', minHeight: 'fit-content' }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Skeleton variant="text" width={120} height={32} />
        <Skeleton variant="text" width={80} height={20} />
      </Box>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Skeleton variant="circular" width={20} height={20} />
        <Skeleton variant="text" width={200} height={24} />
        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Skeleton variant="rectangular" width={70} height={32} sx={{ borderRadius: 2 }} />
        <Skeleton variant="rectangular" width={70} height={32} sx={{ borderRadius: 2 }} />
        <Skeleton variant="text" width={60} height={20} sx={{ alignSelf: 'center' }} />
      </Box>

      <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1, mb: 2 }} />
      <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
    </CardContent>
  </Card>
);

// Loading Overlay Component
const LoadingOverlay = ({ open, message = "Loading..." }) => (
  <Backdrop 
    sx={{ 
      color: '#fff', 
      zIndex: (theme) => theme.zIndex.drawer + 1,
      backdropFilter: 'blur(3px)'
    }} 
    open={open}
  >
    <Box sx={{ textAlign: 'center' }}>
      <CircularProgress size={60} thickness={4} />
      <Typography variant="h6" sx={{ mt: 2, fontWeight: 500 }}>
        {message}
      </Typography>
    </Box>
  </Backdrop>
);

// Inline Loading Button Component
const LoadingButton = ({ loading, onClick, children, ...props }) => (
  <Button
    {...props}
    onClick={onClick}
    disabled={loading}
    startIcon={loading ? <CircularProgress size={16} /> : props.startIcon}
  >
    {children}
  </Button>
);



const NOT_SERVED_REASONS = [
  "Patient refused medication",
  "Patient could not afford medication",
  "Medication out of stock at dispensary",
  "Wrong medication dispensed — returned",
  "Patient discharged before collection",
  "Prescription cancelled by doctor",
  "Patient allergic reaction risk identified",
  "Drug interaction concern raised",
  "Other",
];

const NotServedDialog = ({ open, onClose, medication, onConfirm, loading }) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [otherReason, setOtherReason]       = useState("");
  const [error, setError]                   = useState(false);

  const handleConfirm = () => {
    if (!selectedReason) { setError(true); return; }
    if (selectedReason === "Other" && !otherReason.trim()) { setError(true); return; }
    onConfirm(selectedReason === "Other" ? otherReason : selectedReason);
  };

  const handleClose = () => {
    setSelectedReason("");
    setOtherReason("");
    setError(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{
          px: 3, py: 2,
          background: "linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)",
          color: "white",
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          <Typography variant="h6" fontWeight={700}>⚠️ Medication Not Served</Typography>
          <IconButton onClick={handleClose} sx={{ color: "white" }} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Drug info strip */}
        {medication && (
          <Box sx={{ p: 2, mb: 2.5, borderRadius: 2, bgcolor: "#fef3c7", border: "1px solid #f59e0b" }}>
            <Typography fontWeight={700} fontSize="1rem" color="#92400e">
              {medication.name}
            </Typography>
            <Box display="flex" gap={3} mt={0.5} flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                Qty: <strong>{medication.quantity}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Patient: <strong>{medication.patientName || medication.id}</strong>
              </Typography>
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
          Select reason for not serving: <span style={{ color: "#b91c1c" }}>*</span>
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {NOT_SERVED_REASONS.map((reason) => (
            <Box
              key={reason}
              onClick={() => { setSelectedReason(reason); setError(false); }}
              sx={{
                display: "flex", alignItems: "center", gap: 1.5,
                px: 2, py: 1.25,
                borderRadius: 1.5,
                border: "1.5px solid",
                borderColor: selectedReason === reason ? "#b91c1c" : "#e5e7eb",
                bgcolor: selectedReason === reason ? "#fff1f2" : "#fafafa",
                cursor: "pointer",
                transition: "all 0.15s",
                "&:hover": { borderColor: "#f87171", bgcolor: "#fff5f5" }
              }}
            >
              <Box sx={{
                width: 18, height: 18, borderRadius: "50%",
                border: "2px solid",
                borderColor: selectedReason === reason ? "#b91c1c" : "#d1d5db",
                bgcolor: selectedReason === reason ? "#b91c1c" : "white",
                flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center"
              }}>
                {selectedReason === reason && (
                  <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "white" }} />
                )}
              </Box>
              <Typography variant="body2" color={selectedReason === reason ? "#b91c1c" : "text.primary"} fontWeight={selectedReason === reason ? 600 : 400}>
                {reason}
              </Typography>
            </Box>
          ))}
        </Box>

        {selectedReason === "Other" && (
          <TextField
            fullWidth
            label="Please specify"
            multiline
            rows={2}
            value={otherReason}
            onChange={(e) => { setOtherReason(e.target.value); setError(false); }}
            sx={{ mt: 2 }}
            error={error && !otherReason.trim()}
            helperText={error && !otherReason.trim() ? "Please provide a reason" : ""}
          />
        )}

        {error && !selectedReason && (
          <Typography variant="caption" color="error" sx={{ mt: 1.5, display: "block" }}>
            ⚠ Please select a reason before confirming.
          </Typography>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" onClick={handleClose} disabled={loading} sx={{ flex: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained" color="error"
          onClick={handleConfirm} disabled={loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
          sx={{ flex: 1, fontWeight: 600 }}
        >
          {loading ? "Saving..." : "Confirm Not Served"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default function Prescriptions() {
  const [expandedPrescriptions, setExpandedPrescriptions] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [priorityFilter, setPriorityFilter] = useState('All Priority');
  const [patientTypeFilter, setPatientTypeFilter] = useState('All Types');
  const [searchQuery, setSearchQuery] = useState('');
  const [medicationStatuses, setMedicationStatuses] = useState({});
  const [serveAllStatuses, setServeAllStatuses] = useState({});
  const [medicationServers, setMedicationServers] = useState({});
  const [vitalDialogOpen, setVitalDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [prescriptions, setPrescriptions] = useState([]);

  const [restockCount, setRestockCount] = useState(0);

  // NEW: Preview functionality states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewPrescription, setPreviewPrescription] = useState(null);
  
  // Enhanced Loading States
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [vitalLoading, setVitalLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [medicationActionLoading, setMedicationActionLoading] = useState(new Set());

  // Debounce search query to reduce filtering frequency
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const [currentUser ,setcurrentUser]=useState('');
  // Bump this number whenever a disposition (Serve / Not-Served) happens
  // so the attention dialog re-fetches and possibly auto-closes.
  const [attentionRefreshKey, setAttentionRefreshKey] = useState(0);

  // SEPARATED: Clinical Notes Data Only
  const [vitalsData, setVitalsData] = useState({});
  const [clinicalNotesData, setclinicalNotesData] = useState({});
  //  Laboratory panels and scan findings for the open patient. Not keyed by
  //  patient like the two above: only one dialog is open at a time, and keying
  //  it invited the previous patient's results to be shown under the next one's
  //  name for the moment before the fetch returned.
  const [labHistory, setLabHistory] = useState({
    labs: [], scans: [], labPanelsTotal: 0, scansTotal: 0,
  });

  // Memoized static data to prevent recreation on every render
  const patientVitals = useMemo(() => vitalsData, [vitalsData]);
  const clinicalNotes = useMemo(() => clinicalNotesData, [clinicalNotesData]);

  const [medications, setmedications] = useState([]);

  /* Facility policy on unpaid prescriptions, supplied by the list response.
     Defaults to the strictest position so an older server — or a failed load —
     never leaves an unpaid row looking dispensable. */
  const [unpaidPolicy, setUnpaidPolicy] = useState({
    unpaidVisibility: 'hide',
    canServeUnpaid: false,
  });

  /* Unpaid comes from the server's normalised boolean, never from re-parsing
     status text on the client. The row carries TWO different "status" ideas —
     serve status and payment status — and telling them apart by string is
     exactly how the wrong one gets used. */
  const isUnpaidRow = useCallback(
    (med) => med?.isPaid === false,
    []
  );

  // Unpaid AND the facility does not permit dispensing it.
  const isBlockedRow = useCallback(
    (med) => isUnpaidRow(med) && !unpaidPolicy.canServeUnpaid,
    [isUnpaidRow, unpaidPolicy.canServeUnpaid]
  );


  // ── Not Served state ───────────────────────────────────────────
 const [notServedDialog, setNotServedDialog]                 = useState(false);
const [selectedMedForNotServed, setSelectedMedForNotServed] = useState(null);
const [notServedLoading, setNotServedLoading]               = useState(false);

// ── Reversal dialog triggered from "Not Served" on a paid medication ──
const [notServedReversalDialog, setNotServedReversalDialog] = useState(false);
const [notServedReversalData, setNotServedReversalData]     = useState(null);

  /*  ── WHY THIS IS A REF AND NOT `prescriptions.length` ─────────────────────
   *
   *  Both loaders used to close over a piece of STATE they also cause to
   *  change — `prescriptions.length` / `medications.length` — and list it as a
   *  useCallback dependency. The mount effect then depends on the callbacks, so:
   *
   *      mount -> fetch -> rows arrive -> length 0 becomes 10
   *            -> the callback is rebuilt -> the effect re-runs -> FETCH AGAIN
   *
   *  Every visit to this screen ran both queries TWICE, and settled only because
   *  the second fetch left the length unchanged. It was invisible: the data was
   *  correct and the screen just took twice as long to reach it.
   *
   *  The flag is only ever used to decide whether to show the full-page skeleton
   *  or the inline spinner, so it does not need to be state at all — nothing
   *  renders from it directly. A ref keeps the callbacks stable, the effect runs
   *  once, and each endpoint is called once.                                   */
  const hasLoadedRef = useRef({ prescriptions: false, medications: false });

  // Enhanced prescription loading with multiple loading states
  const loadPrescriptions = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      if (!hasLoadedRef.current.prescriptions) {
        setInitialLoading(true);
      }
    }

    try {
      /*  A one-second `setTimeout` sat here, labelled "simulate network delay
          for demo purposes". It was demo scaffolding that shipped, and it was
          costing a full second on every load and every refresh of this screen —
          more than the query it was waiting for now takes.
          Its twin in loadMedication has gone too.                             */
      const response = await axios.get(loadPharmacyPatients);
      if (response.data.success) {
        hasLoadedRef.current.prescriptions = true;
        setPrescriptions(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error',
        confirmButtonText: 'Try Again',
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          loadPrescriptions(isRefresh);
        }
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
      setRefreshing(false);
    }
    //  No dependencies. See hasLoadedRef above — listing state this function
    //  itself changes is what made the mount effect fire twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMedication = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
      if (!hasLoadedRef.current.medications) {
        setInitialLoading(true);
      }
    }

    try {
      //  The second "simulate network delay" — see loadPrescriptions above.
      const response = await axios.get(loadPharmacyPatientsMed);
      if (response.data.success) {
        hasLoadedRef.current.medications = true;
        setmedications(response.data.results);

        // Policy arrives on the SAME response as the rows, so it always
        // describes the list being shown. Absent => an older server that has
        // no such setting: assume the strictest position (hide / cannot serve)
        // rather than letting unpaid rows look dispensable.
        setUnpaidPolicy({
          unpaidVisibility: response.data.unpaidVisibility || 'hide',
          canServeUnpaid:   response.data.canServeUnpaid === true,
        });
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to Load Data',
        icon: 'error',
        confirmButtonText: 'Try Again',
        showCancelButton: true,
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          loadMedication(isRefresh);
        }
      });
    } finally {
      setLoading(false);
      setInitialLoading(false);
      setRefreshing(false);
    }
    //  No dependencies, for the same reason as loadPrescriptions above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Shared handler for the serve endpoints.
   *
   * A 409 means the bill is unpaid — not that anything is broken. Over a list of
   * drug names a red "Error" reads as the system failing, which sends the
   * pharmacist to IT instead of to the cash desk. So: warning icon, "Payment
   * required", and the offending items named by the server.
   *
   * Always reload afterwards. The cash desk may have taken payment in the time
   * this screen sat open, and a stale list is precisely when this fires.
   */
  const handleServeError = useCallback(async (error, fallbackText) => {
    if (error?.response?.status === 409 && error.response.data?.paymentRequired) {
      await Swal.fire({
        icon: 'warning',
        title: 'Payment required',
        text: error.response.data.message
          || 'Payment has not been recorded for this item yet.',
        confirmButtonColor: '#b45309',
      });
      await loadMedication(true);
      return;
    }
    await Swal.fire({
      title: 'Error',
      text: error?.response?.data?.message || fallbackText,
      icon: 'error',
    });
    console.error(fallbackText, error);
  }, [loadMedication]);

  // Enhanced search with loading indicator
  useEffect(() => {
    if (debouncedSearchQuery !== searchQuery) {
      setSearchLoading(true);
      // Simulate search processing
      const timer = setTimeout(() => {
        setSearchLoading(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [debouncedSearchQuery, searchQuery]);

  useEffect(() => {
    loadPrescriptions();
    loadMedication()
  }, [loadPrescriptions, loadMedication]);

  // Memoized search normalization to avoid repeated toLowerCase calls
  const normalizedSearchTerms = useMemo(() => {
    if (!debouncedSearchQuery) return null;
    return debouncedSearchQuery.toLowerCase().trim();
  }, [debouncedSearchQuery]);

  // Memoized medication helpers to prevent recalculation
  const getMedicationsForPrescription = useCallback((prescriptionId) => {
    return medications.filter(med => med.prescriptionId === prescriptionId);
  }, [medications]);

  const getMedicationStatus = useCallback((prescriptionId, medicationId, originalStatus) => {
    const key = `${prescriptionId}-${medicationId}`;
    return medicationStatuses[key] || originalStatus;
  }, [medicationStatuses]);

  const getPatientClinicalNotes = (patientId) => {
    const noteData = clinicalNotes[patientId];
    
    // If no data exists, return empty array
    if (!noteData) {
      return [];
    }
    
    // Convert the single note object into an array format expected by the UI
    return [{
      date: noteData.date,
      time: noteData.time,
      author: noteData.doc_name,
      chiefComplaint: noteData.symptorms, // Maps to symptoms
      hpi: noteData.his_comp, // Maps to history/complaint
      pastMedicalHistory: noteData.ex_result,
      Procedure_: noteData.Procedure_,
      documenting: noteData.documenting
    }];
  };

  // Memoized medication counts calculation
  const medicationCountsCache = useMemo(() => {
    const cache = {};
    prescriptions.forEach(prescription => {
      const prescriptionMedications = getMedicationsForPrescription(prescription.id);
      let served = 0, pending = 0, completed = 0;

      prescriptionMedications.forEach(medication => {
        const currentStatus = getMedicationStatus(prescription.id, medication.id, medication.status);
        switch (currentStatus) {
          case 'served': served++; break;
          case 'completed': completed++; break;
          default: pending++;
        }
      });

      cache[prescription.id] = { 
        served, 
        pending, 
        completed, 
        total: prescriptionMedications.length 
      };
    });
    return cache;
  }, [prescriptions, getMedicationsForPrescription, getMedicationStatus]);

  const getMedicationCounts = useCallback((prescriptionId) => {
    return medicationCountsCache[prescriptionId] || { served: 0, pending: 0, completed: 0, total: 0 };
  }, [medicationCountsCache]);

  // Optimized filtering with memoization - NOW INCLUDES MEDICATION COUNT FILTER
  const filteredPrescriptions = useMemo(() => {
    let filtered = prescriptions;

    // Filter out prescriptions with zero medications FIRST
    filtered = filtered.filter(prescription => {
      const counts = medicationCountsCache[prescription.id];
      return counts && counts.total > 0;
    });

    // Apply search filter
    if (normalizedSearchTerms) {
      filtered = filtered.filter(prescription => {
        const normalizedPatientName = prescription.patientName?.toLowerCase() || '';
        const normalizedId = prescription.id?.toLowerCase() || '';
        
        return normalizedPatientName.includes(normalizedSearchTerms) ||
               normalizedId.includes(normalizedSearchTerms) ||
               prescription.id?.toString().includes(normalizedSearchTerms);
      });
    }

    // Apply status filter
    if (statusFilter !== 'All Status') {
      filtered = filtered.filter(prescription => 
        prescription.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Apply priority filter
    if (priorityFilter !== 'All Priority') {
      filtered = filtered.filter(prescription => 
        prescription.priority?.toLowerCase() === priorityFilter.toLowerCase()
      );
    }

    // Apply patient type filter
    if (patientTypeFilter !== 'All Types') {
      filtered = filtered.filter(prescription => 
        prescription.patientType?.toLowerCase() === patientTypeFilter.toLowerCase()
      );
    }

    return filtered;
  }, [prescriptions, medicationCountsCache, normalizedSearchTerms, statusFilter, priorityFilter, patientTypeFilter]);

  // Status and priority color helpers
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'urgent': return 'error';
      case 'pending': return 'info';
      case 'completed': return 'success';
      case 'cancelled': return 'default';
      case 'served': return 'success';
      default: return 'default';
    }
  }, []);

  // Group medications by date helper
  const groupMedicationsByDate = useCallback((medications) => {
    return medications.reduce((groups, medication) => {
      const date = medication.administrationDate || 'No Date Specified';
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(medication);
      return groups;
    }, {});
  }, []);

  //  Add visual indicator for served medications (optional enhancement)
  const getCheckboxStyles = (originalStatus, currentStatus) => {
    if (originalStatus === 'served' || currentStatus === 'served') {
      return {
        color: 'success.main',
        '&.Mui-disabled': {
          color: 'success.light',
        },
        '&.Mui-checked.Mui-disabled': {
          color: 'success.main',
        }
      };
    }
    return {};
  };

  // Serve all functionality
  const getServeAllStatus = useCallback((prescriptionId) => {
    const prescriptionMedications = getMedicationsForPrescription(prescriptionId);
    if (prescriptionMedications.length === 0) return false;

    // Only consider non-completed medications
    const activeIngredients = prescriptionMedications.filter(med => med.status !== 'completed');
    if (activeIngredients.length === 0) return false;

    // Check if all active medications are served (either originally or currently)
    return activeIngredients.every(medication => {
      const key = `${prescriptionId}-${medication.id}`;
      const currentStatus = medicationStatuses[key] || medication.status;
      return currentStatus === 'served';
    });
  }, [getMedicationsForPrescription, medicationStatuses]);

  useEffect(() => {
    const savedUserInfo = localStorage.getItem('userInfo');
    if (savedUserInfo) {
      const userInfo = JSON.parse(savedUserInfo);
      const userName = userInfo.name || '';
      setcurrentUser(userName)
    } 
  }, []);

  //Pending Medications
  const handleServeAllPendingOnly = useCallback(async (prescriptionId, checked) => {
    const prescriptionMedications = getMedicationsForPrescription(prescriptionId);
    if (!prescriptionMedications) return;

    const newStatuses = { ...medicationStatuses };
    const newServers = { ...medicationServers };
    
    // Only update pending medications
    const medicationsToUpdate = [];
    
    prescriptionMedications.forEach(medication => {
      // Skip rows the facility will not let us dispense. Without this, one
      // unpaid item makes the server refuse the WHOLE batch with a 409 and the
      // pharmacist cannot dispense even the paid lines. "Serve all" therefore
      // means "serve everything payable"; the badge explains the remainder.
      // (Only applies when serving — un-serving is never payment-gated.)
      if (checked && isBlockedRow(medication)) return;

      if (medication.status !== 'completed' && medication.status !== 'served') {
        const key = `${prescriptionId}-${medication.id}`;
        newStatuses[key] = checked ? 'served' : 'pending';
        
        if (checked) {
          newServers[key] = {
            user: currentUser,
            timestamp: new Date().toLocaleString()
          };
        } else {
          delete newServers[key];
        }
        
        medicationsToUpdate.push({
          medicationId: medication.id,
          status: checked ? '1' : '0',
          servedBy: checked ? currentUser : null,
          servedAt: checked ? new Date() : null
        });
      }
    });

    try {
      const response = await axios.put(prescriptionServeAll, {
        medications: medicationsToUpdate, 
        serveAll: false, // Don't mark as "serve all" since some medications are excluded
        userId: currentUser, 
        timestamp: new Date()
      });
      
      if (response.data.success) {
        // Update local state
        setMedicationStatuses(newStatuses);
        setMedicationServers(newServers);

        // RELOAD MEDICATIONS DATA TO GET UPDATED SERVER INFO
        await loadMedication(true);
        setAttentionRefreshKey(k => k + 1);

        // Show success message
        successMessage('Update', 'Pending medications updated successfully');
      }

    } catch (error) {
      await handleServeError(error, 'Failed to update pending medications');
    }
  }, [
    getMedicationsForPrescription,
    medicationStatuses,
    medicationServers,
    currentUser,
    loadMedication,
    handleServeError,
    isBlockedRow,
    setMedicationStatuses,
    setMedicationServers,
    successMessage
  ]);

  const handleServeAllChange = useCallback(async (prescriptionId, checked) => {
    const prescriptionMedications = getMedicationsForPrescription(prescriptionId);
    if (!prescriptionMedications) return;

    // If unchecking "Serve All", check if any medications are already served
    if (!checked) {
      const alreadyServedMedications = prescriptionMedications.filter(med => 
        med.status === 'served' || getMedicationStatus(prescriptionId, med.id, med.status) === 'served'
      );
      
      if (alreadyServedMedications.length > 0) {
        await Swal.fire({
          title: 'Cannot Unserve All',
          text: `${alreadyServedMedications.length} medication(s) have already been served and cannot be unserved. Only pending medications will be updated.`,
          icon: 'warning',
          confirmButtonText: 'Continue with Pending Only',
          showCancelButton: true,
          cancelButtonText: 'Cancel'
        }).then(async (result) => {
          if (result.isConfirmed) {
            // Only update pending medications
            await handleServeAllPendingOnly(prescriptionId, false);
          }
        });
        return;
      }
    }

    const newStatuses = { ...medicationStatuses };
    const newServers = { ...medicationServers };
    
    // Collect medications that will be updated (exclude already served when unchecking)
    const medicationsToUpdate = [];
    
    prescriptionMedications.forEach(medication => {
      // Same reason as handleServeAllPendingOnly: keep unpayable rows out of
      // the batch so one unpaid item does not 409 the whole prescription and
      // leave the pharmacist unable to dispense the paid lines.
      if (checked && isBlockedRow(medication)) return;

      if (medication.status !== 'completed') {
        // Skip already served medications when unchecking
        if (!checked && medication.status === 'served') {
          return;
        }
        
        const key = `${prescriptionId}-${medication.id}`;
        newStatuses[key] = checked ? 'served' : 'pending';
        
        if (checked) {
          newServers[key] = {
            user: currentUser,
            timestamp: new Date().toLocaleString()
          };
        } else {
          delete newServers[key];
        }
        
        medicationsToUpdate.push({
          medicationId: medication.id,
          status: checked ? '1' : '0',
          servedBy: checked ? currentUser : null,
          servedAt: checked ? new Date() : null
        });
      }
    });

    try {
      const response = await axios.put(prescriptionServeAll, {
        medications: medicationsToUpdate, 
        serveAll: checked, 
        userId: currentUser, 
        timestamp: new Date()
      });
      
      if (response.data.success) {
        // Update local state
        setMedicationStatuses(newStatuses);
        setMedicationServers(newServers);
        setServeAllStatuses(prev => ({
          ...prev,
          [prescriptionId]: checked
        }));

        // RELOAD MEDICATIONS DATA TO GET UPDATED SERVER INFO
        await loadMedication(true);
        setAttentionRefreshKey(k => k + 1);

        // Show success message
        successMessage('Update', response.data.message ||
          `Prescription ${checked ? 'served' : 'updated'} successfully`);
      }
      
    } catch (error) {
      await handleServeError(
        error,
        `Failed to ${checked ? 'serve' : 'update'} prescription medications`
      );
    }
  }, [
    getMedicationsForPrescription,
    medicationStatuses,
    medicationServers,
    currentUser,
    loadMedication,
    handleServeError,
    isBlockedRow,
    getMedicationStatus,
    setMedicationStatuses,
    setMedicationServers,
    setServeAllStatuses,
    successMessage
  ]);

  const handleNotServedConfirm = async (reason) => {
  if (!selectedMedForNotServed) return;
  setNotServedLoading(true);

  try {
    const response = await axios.put(
      notServedMedicationUrl(selectedMedForNotServed.id),
      { reason, pharmacist: currentUser }
    );

    if (response.data.success) {

      // ── Close not-served dialog ──────────────────────────────────────
      setNotServedDialog(false);
      setSelectedMedForNotServed(null);

      // ── Reload medications ───────────────────────────────────────────
      await loadMedication(true);
      // Refresh the attention badge so the patient drops off the pending
      // list the moment every med has a final disposition.
      setAttentionRefreshKey(k => k + 1);

      if (response.data.requiresReversal && response.data.reversalData) {
        // ── Paid medication: ask pharmacist to open reversal now ────────
        const { isConfirmed } = await Swal.fire({
          title: '⚠️ Payment Reversal Needed',
          html: `
            <div style="text-align:left;">
              <p style="margin-bottom:12px; color:#555;">
                This medication was already 
                <strong style="color:#dc2626; text-transform:uppercase;">
                  ${response.data.billingStatus}
                </strong>. 
                A reversal must be submitted so the billing team can process a refund.
              </p>
              <div style="
                background:#fff3e0;
                border-left:4px solid #f59e0b;
                border-radius:6px;
                padding:12px 14px;
                font-weight:600;
                color:#92400e;">
                ${response.data.reversalData.details}
              </div>
              <p style="margin-top:14px; color:#374151; font-size:0.9rem;">
                Click <strong>"Open Reversal"</strong> to submit the reversal request now.
              </p>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: '↩ Open Reversal',
          cancelButtonText: 'Later',
          confirmButtonColor: '#dc2626',
          reverseButtons: true,
          allowOutsideClick: false,
          width: '480px'
        });

        if (isConfirmed) {
          // ── Open ReversalDialog with the returned billing data ───────ß─
          setNotServedReversalData(response.data.reversalData);
          setNotServedReversalDialog(true);
        }

      } else {
        // ── Unpaid: simple success toast ────────────────────────────────
        Swal.fire({
          icon: 'info',
          title: 'Recorded',
          text: response.data.message,
          timer: 2000,
          showConfirmButton: false
        });
      }

    } else {
      Swal.fire('Error', response.data.message, 'error');
    }

  } catch (err) {
    Swal.fire('Error', err.response?.data?.message || 'Failed to update', 'error');
  } finally {
    setNotServedLoading(false);
  }
};

  // Optimized toggle function
  const togglePrescription = useCallback((prescriptionId) => {
    setExpandedPrescriptions(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(prescriptionId)) {
        newExpanded.delete(prescriptionId);
      } else {
        newExpanded.add(prescriptionId);
      }
      return newExpanded;
    });
  }, []);

  // Enhanced medication status handlers with loading states
  const handleMedicationStatusChange = useCallback(async (prescriptionId, medicationId, checked) => {
    const key = `${prescriptionId}-${medicationId}`;
    
    // Find the original medication to check its status
    const originalMedication = medications.find(med => med.id === medicationId);
    const currentStatus = getMedicationStatus(prescriptionId, medicationId, originalMedication?.status);
    
    // Prevent unserving if medication was already served
    if (!checked && (originalMedication?.status === 'served' || currentStatus === 'served')) {
      await Swal.fire({
        title: 'Cannot Unserve',
        text: 'Medications that have been served cannot be unserved.',
        icon: 'warning',
        confirmButtonText: 'Understood'
      });
      return;
    }
    
    // Add to loading set
    setMedicationActionLoading(prev => new Set(prev).add(key));
    
    try {
      // API call to update medication status in database
      const response = await axios.put(updateMedicationStatutt(medicationId), {
        prescriptionId: prescriptionId,
        medicationId: medicationId,
        status: checked ? '1' : '0',
        servedBy: checked ? currentUser : null,
        servedAt: checked ? new Date() : null,
        timestamp: new Date()
      });
      
      if (response.data.success) {
        // Update local state with server response data
        setMedicationStatuses(prev => ({
          ...prev,
          [key]: checked ? 'served' : 'pending'
        }));
        
        if (checked && response.data.serverInfo) {
          // Use server response data instead of local timestamp
          setMedicationServers(prev => ({
            ...prev,
            [key]: {
              user: response.data.serverInfo.servedBy || currentUser,
              timestamp: response.data.serverInfo.servedAt || new Date().toLocaleString()
            }
          }));
        } else if (!checked) {
          setMedicationServers(prev => {
            const newServers = { ...prev };
            delete newServers[key];
            return newServers;
          });
        }

        // RELOAD MEDICATIONS DATA TO GET UPDATED SERVER INFO
        await loadMedication(true);
        
      }
      
    } catch (error) {
      await handleServeError(error, 'Failed to update medication status');

    } finally {
      // Remove from loading set
      setMedicationActionLoading(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  }, [currentUser, loadMedication, handleServeError, medications, getMedicationStatus, setMedicationStatuses, setMedicationServers, setMedicationActionLoading]);

  // Enhanced vital dialog handlers with loading
  const handleOpenVitalDialog = useCallback(async (prescription) => {
    setSelectedPatient(prescription);
    setVitalDialogOpen(true);
    setVitalLoading(true);

    try {
      const vitals = {}
      const clinica = {}
      const id = prescription.id
     
      const response = await axios.post(loadPharmacyVitals, {id});
      
      if (response.data.success) {
        const v = response.data.results[0] || {};
        /*  ── THESE VITALS ARE METRIC. THEY WERE BEING READ AS IMPERIAL ───────
         *
         *  Every line here assumed US units and converted away from them, so
         *  the card showed nonsense that looked like a real reading:
         *
         *      temperature   36.8 read as °F  ->  "36.8°F (2.7°C)"
         *      weight        58 kg read as lb ->  "58 lbs (26.3 kg)"
         *      height        145 cm read as in->  "12'1" (368 cm)"
         *      BMI           imperial formula ->  wrong by a factor of ~703
         *
         *  2.7°C is a corpse. It was reported as "doesn't look professional",
         *  which is generous — it is a clinical display error.
         *
         *  The data settles it. Across all 346,334 rows in `vitals`:
         *
         *      average temperature   37.3   (Celsius. Fahrenheit would be ~98)
         *      average weight        58.1   (kg. Pounds would be ~150)
         *      average height       145.5   (cm. Inches would be ~65)
         *
         *  So the values are shown as recorded, with the unit they are actually
         *  in, and nothing is converted. `bmi` and `spo2` are columns the table
         *  already keeps and nothing was reading — the BMI in particular is the
         *  one the triage screen calculated and stored, which beats recomputing
         *  it here from a height that may be missing.                          */
        const val = (x) => {
          const s = String(x ?? '').trim();
          return s && s !== '0' && s !== '-' ? s : null;
        };
        vitals[id] = {
          bloodPressure:   val(v.pre)   ? `${val(v.pre)} mmHg` : 'N/A',
          heartRate:       val(v.pulse) ? `${val(v.pulse)} bpm` : 'N/A',
          temperature:     val(v.tem)   ? `${val(v.tem)} °C` : 'N/A',
          respiratoryRate: val(v.res)   ? `${val(v.res)} breaths/min` : 'N/A',
          oxygenSaturation: val(v.spo2) ? `${val(v.spo2)} %` : 'N/A',
          weight:          val(v.we)    ? `${val(v.we)} kg` : 'N/A',
          height:          val(v.high)  ? `${val(v.high)} cm` : 'N/A',
          //  The stored BMI first — it is what triage recorded. Computed from
          //  kg/m² only as a fallback, and only when both parts are there.
          bmi: val(v.bmi)
            ? val(v.bmi)
            : (val(v.we) && val(v.high)
              ? (Number(v.we) / ((Number(v.high) / 100) ** 2)).toFixed(1)
              : 'N/A'),
          glucoseLevel: val(v.rbs) ? `${val(v.rbs)} mmol/L (RBS)`
            : (val(v.fbs) ? `${val(v.fbs)} mmol/L (FBS)` : 'N/A'),
          lastUpdated: v.time ? v.time : 'N/A'
        };

        setVitalsData(vitals);
      }

      const response2 = await axios.post(loadPharmacyclinicalNotes, {id});

      if (response2.data.success) {
        const v = response2.data.results[0];
        clinica[id] = {
          date: v.dat ? v.dat : 'N/A',
          time: v.time ? v.time : 'N/A',
          doc_name: v.doc_name ? v.doc_name : 'N/A',
          symptorms: v.symptorms ? v.symptorms : 'N/A',
          his_comp: v.his_comp ? v.his_comp :'N/A',
          ex_result: v.ex_result ? v.ex_result : 'N/A',
          Procedure_: v.procedure_ ? v.procedure_ : 'N/A', // Add to DB if needed
          documenting: v.documenting ? v.documenting : 'N/A',
        };

        setclinicalNotesData(clinica);
      }

      /*  ── LABORATORY AND SCAN HISTORY ──────────────────────────────────────
       *  Fetched here so the dialog is one complete picture of the patient
       *  rather than vitals and notes with the results left in another room.
       *
       *  Its own try/catch, and deliberately NOT allowed to fail the dialog: a
       *  server that predates this endpoint 404s, and the vitals and notes that
       *  did load are still worth showing. The sections simply do not appear.  */
      try {
        const response3 = await axios.post(loadPharmacyPatientHistory, { id });
        if (response3.data.success) {
          setLabHistory({
            labs:  response3.data.labs  || [],
            scans: response3.data.scans || [],
            labPanelsTotal: response3.data.labPanelsTotal || 0,
            scansTotal:     response3.data.scansTotal || 0,
          });
        }
      } catch (histErr) {
        setLabHistory({ labs: [], scans: [], labPanelsTotal: 0, scansTotal: 0 });
      }

      /*  A third one-second sleep sat here, AFTER both requests had returned and
          the state was already set — so it did nothing but hold the Vitals
          dialog's spinner open for an extra second once the data was on screen.
          Same demo scaffolding as the two in the loaders above.               */
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed ',
        icon: 'error'
      });
    } finally {
      setVitalLoading(false);
    }
  }, []);

  const handleCloseVitalDialog = useCallback(() => {
    setVitalDialogOpen(false);
    setSelectedPatient(null);
    setVitalLoading(false);
  }, []);

  // Get patient vitals with memoization
  const getPatientVitals = useCallback((patientId) => {
    return patientVitals[patientId] || null;
  }, [patientVitals]);

  // Refresh handler
  const handleRefresh = useCallback(() => {
    loadPrescriptions(true);
    loadMedication(true);
  }, [loadPrescriptions, loadMedication]);

  const [selectedPatientt ,setselectedPatientt]=useState()
  const [openmedHis, setopenmedHis] = useState(false);

  const openPatientMed =(prescription)=>{
    setselectedPatientt(prescription.id)
    setopenmedHis(true)
    //console.log(prescription)
  }

  const closePatientMed =()=>{
    setopenmedHis(false)
  }

  // NEW: Preview functionality handlers
  const handleOpenPreviewDialog = useCallback((prescription) => {
    setPreviewPrescription(prescription);
    setPreviewDialogOpen(true);

    // Record that this pharmacist is now actively reviewing this patient's
    // prescription. The row in pharmacy_attention stays unresolved until
    // every medication for that visit-date has Serve != '0'. Fire-and-
    // forget — never block the UI — but surface errors in the console so
    // we can debug if the row isn't being recorded.
    try {
      // Prefer today's date (the meds query uses CURDATE) so the visit_date
      // we store always matches medication.dat for new prescriptions.
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const rawDate = prescription?.dat || prescription?.visitDate || prescription?.reg_date || '';
      const visitDate = (typeof rawDate === 'string' && /^\d{4}-\d{2}-\d{2}/.test(rawDate))
        ? rawDate.slice(0, 10)
        : todayStr;
      if (currentUser && prescription?.id && visitDate) {
        axios.post(pharmacyAttentionOpen, {
          pharmacist: currentUser,
          patientId:  prescription.id,
          visitDate,
        })
        .then((r) => console.log('[pharmacy attention] open OK', r.data))
        .catch((err) => console.error('[pharmacy attention] open FAILED', err?.response?.status, err?.response?.data || err.message));
      } else {
        console.warn('[pharmacy attention] open SKIPPED — missing inputs', { currentUser, patientId: prescription?.id, visitDate });
      }
    } catch (e) {
      console.error('[pharmacy attention] open threw', e);
    }
  }, [currentUser]);

  // Soft-block close: when the pharmacist tries to leave with medications
  // still in Serve='0', surface a 3-option prompt. The patient already
  // sits in pharmacy_attention from the open call above, so "Come back
  // later" is just an explicit acknowledgement — it leaves the row
  // unresolved and the attention dialog will surface it on next sign-in.
  const handleClosePreviewDialog = useCallback(async () => {
    try {
      const meds = previewPrescription
        ? getMedicationsForPrescription(previewPrescription.id) || []
        : [];
      const pending = meds.filter(m => {
        const s = String(m.Serve ?? m.status ?? '');
        // Treat blank / '0' / 'pending' as unresolved
        return !s || s === '0' || s.toLowerCase() === 'pending';
      });

      if (pending.length > 0) {
        const result = await Swal.fire({
          icon: 'warning',
          title: `${pending.length} medication${pending.length === 1 ? '' : 's'} still pending`,
          html: `<p style="text-align:left;color:#475569;font-size:13px;">
            Each medication should be either <strong>Served</strong> or
            <strong>Marked Not Served</strong> with a reason before you leave
            this patient. If you need to attend to another patient right now,
            choose <strong>Come back later</strong> — this patient will
            stay on your <strong>Pending Attention</strong> list and pop up
            on your next sign-in.</p>`,
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Stay & resolve',
          denyButtonText:    'Come back later',
          cancelButtonText:  'Cancel',
          confirmButtonColor: '#41a386',
          denyButtonColor:    '#b45309',
          cancelButtonColor:  '#94a3b8',
          reverseButtons: true,
          allowOutsideClick: false,
          allowEscapeKey:    false,
        });

        if (result.isConfirmed) return;            // Stay & resolve → keep dialog open
        if (result.dismiss === Swal.DismissReason.cancel) return; // Cancel → keep dialog open
        // result.isDenied === true → Come back later: proceed to close,
        // pharmacy_attention row stays unresolved so it reappears later.
      }
    } catch (_) {
      // If the prompt fails for any reason, fall through and close — never
      // trap the pharmacist behind a broken dialog.
    }

    setPreviewDialogOpen(false);
    setPreviewPrescription(null);
    setAttentionRefreshKey(k => k + 1);
  }, [previewPrescription, getMedicationsForPrescription]);

  return (
    <Box sx={{
      bgcolor: '#f8fafc', minHeight: '100vh',
      py: 2.5,
      // Comfortable horizontal margins on every breakpoint — pharmacists asked
      // for "more space at both ends". We use the page width up to ~1700px and
      // keep a sensible padding instead of MUI's centred Container.
      px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
    }}>
      {/* Global Loading Overlay */}
      <LoadingOverlay open={initialLoading} message="Loading prescriptions…" />

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
        }}>
          <Box>
            <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
              Pharmacy · Prescriptions
            </Typography>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
              Prescription management
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25, maxWidth: 760 }}>
              Search a patient by ID, expand a card to dispense individual medications, or open the
              preview to dispense in bulk. Use the type filter to focus on inpatients or outpatients.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title={restockCount > 0 ? `${restockCount} restock notification${restockCount === 1 ? '' : 's'}` : 'Restock notifications'} arrow>
              <IconButton
                size="small"
                color={restockCount > 0 ? 'error' : 'default'}
                sx={{
                  position: 'relative',
                  border: `1px solid ${BORDER}`, bgcolor: '#fff',
                  color: restockCount > 0 ? '#dc2626' : MUTED,
                  '&:hover': { bgcolor: '#f1f5f9' },
                }}
              >
                <Badge badgeContent={restockCount} color="error" max={99}>
                  <Warning sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <LoadingButton
              loading={refreshing}
              onClick={handleRefresh}
              variant="outlined"
              startIcon={<Refresh sx={{ fontSize: 18 }} />}
              size="small"
              sx={{
                textTransform: 'none', fontWeight: 600,
                borderColor: BORDER, color: MUTED,
                '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
              }}
            >
              Refresh
            </LoadingButton>
          </Box>
        </Box>
        {refreshing && (
          <Box sx={{ width: '100%', mt: 1.5 }}>
            <LinearProgress sx={{ height: 3, borderRadius: 999, bgcolor: BORDER, '& .MuiLinearProgress-bar': { bgcolor: PRIMARY } }} />
          </Box>
        )}
      </Box>

      {/* Search & filter bar */}
      <Paper elevation={0} sx={{
        p: 1.5, mb: 2, borderRadius: 2,
        border: `1px solid ${BORDER}`, bgcolor: '#fff',
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1,
      }}>
        <TextField
          size="small"
          placeholder="Search by patient ID or name…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          disabled={initialLoading}
          sx={{ flex: { xs: '1 1 100%', md: '1 1 380px' }, minWidth: 240 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {searchLoading
                  ? <CircularProgress size={16} sx={{ color: PRIMARY }} />
                  : <Search sx={{ fontSize: 18, color: '#94a3b8' }} />}
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton onClick={() => setSearchQuery('')} edge="end" size="small">
                  <Clear sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{
          display: 'inline-flex', p: 0.4, borderRadius: 1.5,
          bgcolor: '#f1f5f9', border: `1px solid ${BORDER}`,
        }}>
          {['All Types', 'Inpatient', 'Outpatient'].map((label) => {
            const value = label;
            const selected = patientTypeFilter === value;
            return (
              <Button
                key={value}
                onClick={() => setPatientTypeFilter(value)}
                disabled={initialLoading}
                size="small"
                sx={{
                  minWidth: 84, height: 30,
                  px: 1.25, borderRadius: 1,
                  textTransform: 'none', fontSize: '0.78rem', fontWeight: 700,
                  color: selected ? '#fff' : MUTED,
                  bgcolor: selected ? PRIMARY : 'transparent',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: selected ? PRIMARY_DARK : 'rgba(15,23,42,0.04)',
                    boxShadow: 'none',
                  },
                  '&.Mui-disabled': {
                    color: selected ? '#fff' : '#cbd5e1',
                    bgcolor: selected ? PRIMARY : 'transparent',
                  },
                }}
              >
                {label === 'All Types' ? 'All' : label}
              </Button>
            );
          })}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.74rem', color: MUTED }}>
          {initialLoading
            ? 'Loading…'
            : searchLoading
              ? 'Searching…'
              : `${filteredPrescriptions.length} of ${prescriptions.filter(p => getMedicationCounts(p.id).total > 0).length} shown`}
        </Typography>
      </Paper>

      {/* Initial Loading State with Skeletons */}
      {initialLoading && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              xl: 'repeat(3, 1fr)',
            },
            gap: 1.5,
            width: '100%',
          }}
        >
          {[...Array(6)].map((_, index) => (
            <PrescriptionCardSkeleton key={index} />
          ))}
        </Box>
      )}

      {/* Prescription Cards */}
      {!initialLoading && (
        <Fade in={!initialLoading}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, 1fr)',
                xl: 'repeat(3, 1fr)',
              },
              gap: 1.5,
              width: '100%',
            }}
          >
            {filteredPrescriptions.map((prescription) => {
              const counts = getMedicationCounts(prescription.id);
              const prescriptionMedications = getMedicationsForPrescription(prescription.id);
              const isExpanded = expandedPrescriptions.has(prescription.id);
              
              const isInpatient = (prescription.patientType || '').toLowerCase() === 'inpatient';
              return (
                <Paper
                  key={prescription.id}
                  elevation={0}
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    border: `1px solid ${BORDER}`,
                    bgcolor: '#fff',
                    overflow: 'hidden',
                    transition: 'border-color 0.15s, transform 0.15s',
                    '&:hover': { borderColor: PRIMARY },
                  }}
                >
                  <Box sx={{ p: 1.75 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25, gap: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                          <Typography sx={LABEL_SX}>{isInpatient ? 'Inpatient' : 'Outpatient'}</Typography>
                          {prescription.status && <StatusPill status={prescription.status} />}
                        </Stack>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }} noWrap>
                          {prescription.id}
                        </Typography>
                        <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: STRONG, mt: 0.25 }} noWrap title={prescription.patientName}>
                          <Person sx={{ fontSize: 14, color: MUTED, verticalAlign: 'middle', mr: 0.5 }} />
                          {prescription.patientName?.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ color: MUTED }}>
                          <AccessTime sx={{ fontSize: 14 }} />
                          <Typography sx={{ fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums' }}>
                            {prescription.time}
                          </Typography>
                        </Stack>
                        {prescription.type && (
                          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end" sx={{ color: MUTED, mt: 0.4 }}>
                            <LocationOn sx={{ fontSize: 14 }} />
                            <Typography sx={{ fontSize: '0.74rem' }} noWrap title={prescription.type}>
                              {prescription.type}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    </Box>

                    {(prescription.gender || prescription.age) && (
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: MUTED, mb: 1 }}>
                        <WcIcon sx={{ fontSize: 14 }} />
                        <Typography sx={{ fontSize: '0.74rem' }}>
                          {prescription.gender}{prescription.age ? ` · Age ${prescription.age}` : ''}
                        </Typography>
                      </Stack>
                    )}

                    {/*  ── WHAT IS BEING TREATED ──────────────────────────────
                         The pharmacist dispenses against a diagnosis. It was
                         only in History, one dialog per patient, so a ward of
                         forty-eight cards meant forty-eight dialogs to find out
                         what the drugs were for.

                         THE DATE IS SHOWN WHENEVER IT IS NOT TODAY, and that is
                         the important half. These are the patient's most recent
                         diagnoses, which on a long inpatient stay can be weeks
                         old — the ward's own data has admissions carrying a bulk
                         timestamp while the real diagnoses sit weeks before it.
                         An old diagnosis printed beside today's prescription
                         with no date reads as today's, and that is worse than
                         showing nothing at all. */}
                    {(prescription.diagnoses || prescription.procedures) && (
                      <Box sx={{
                        display: 'flex', gap: 0.75, alignItems: 'flex-start',
                        mb: 1, px: 1, py: 0.75, borderRadius: 1.5,
                        bgcolor: 'rgba(234,179,8,0.08)',
                        border: '1px solid rgba(234,179,8,0.28)',
                      }}>
                        <LocalHospital sx={{ fontSize: 14, color: '#a16207', mt: '2px', flexShrink: 0 }} />
                        <Box sx={{ minWidth: 0 }}>
                          {/*  Whichever the patient has — often both, sometimes
                               only one. Each carries ITS OWN date, because a
                               diagnosis from last week and a procedure from last
                               year routinely sit on the same card and one shared
                               label would misdate one of them. */}
                          {[
                            ['Diagnosis', prescription.diagnoses, prescription.diagnosis_date],
                            ['Procedure', prescription.procedures, prescription.procedure_date],
                          ].filter(([, text]) => text).map(([label, text, when], i) => {
                            const d = String(when || '').slice(0, 10);
                            const today = new Date().toISOString().slice(0, 10);
                            //  Shown whenever it is not today. On a long stay
                            //  these can be months old, and an old entry printed
                            //  beside today's prescription with no date reads as
                            //  today's — worse than showing nothing.
                            const stamp = d && d !== today ? ` · ${d}` : '';
                            return (
                              <Box key={label} sx={{ mt: i ? 0.6 : 0 }}>
                                <Typography sx={{
                                  fontSize: '0.62rem', fontWeight: 800, color: '#a16207',
                                  letterSpacing: '0.04em', textTransform: 'uppercase',
                                  lineHeight: 1.2,
                                }}>
                                  {label}{stamp}
                                </Typography>
                                <Typography sx={{
                                  fontSize: '0.76rem', color: STRONG, lineHeight: 1.35, mt: '1px',
                                }}>
                                  {text}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    )}

                    {/* Med count strip */}
                    <Box sx={{
                      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: 0.75, mb: 1.25, p: 0.75, bgcolor: '#f8fafc',
                      borderRadius: 1.5, border: `1px solid ${BORDER}`,
                    }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={LABEL_SX}>Total</Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STRONG, lineHeight: 1.1 }}>
                          {counts.total}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center', borderLeft: `1px solid ${BORDER}`, borderRight: `1px solid ${BORDER}` }}>
                        <Typography sx={LABEL_SX}>Served</Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STATUS_COLORS.served.strong, lineHeight: 1.1 }}>
                          {counts.served}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={LABEL_SX}>Pending</Typography>
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: STATUS_COLORS.pending.strong, lineHeight: 1.1 }}>
                          {counts.pending}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Allergies Alert */}
                    {prescription.allergies && prescription.allergies.length > 0 && (
                      <Alert
                        severity="error"
                        icon={<Warning sx={{ fontSize: 18 }} />}
                        sx={{
                          mb: 1.25, py: 0.5, borderRadius: 1.5,
                          '& .MuiAlert-message': { py: 0.25, fontSize: '0.78rem' },
                        }}
                      >
                        <strong>Allergies:</strong> {prescription.allergies.join(', ')}
                      </Alert>
                    )}

                    {/* Show / hide button */}
                    <Box>
                      <Button
                        onClick={() => togglePrescription(prescription.id)}
                        variant="outlined"
                        endIcon={isExpanded ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                        fullWidth
                        size="small"
                        sx={{
                          mb: 1.25,
                          textTransform: 'none', fontWeight: 700,
                          borderColor: BORDER, color: STRONG,
                          bgcolor: isExpanded ? PRIMARY_BG : 'transparent',
                          '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
                        }}
                      >
                        {isExpanded ? 'Hide medications' : `Show medications (${counts.total})`}
                      </Button>

                      {/* Only render expanded content when actually expanded */}
                      {isExpanded && (
                        <Collapse in={isExpanded}>
                          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                Prescription
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {prescriptionMedications.length} medication{prescriptionMedications.length !== 1 ? 's' : ''}
                                </Typography>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={getServeAllStatus(prescription.id)}
                                      onChange={(e) => handleServeAllChange(prescription.id, e.target.checked)}
                                      size="small"
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                      Serve All
                                    </Typography>
                                  }
                                  sx={{ m: 0 }}
                                />
                              </Box>
                            </Box>
                            
                            {/* Medications grouped by date with scroll */}
                            <Box 
                              sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                gap: 1.5,
                                maxHeight: '400px',
                                overflowY: 'auto',
                                pr: 1
                              }}
                            >
                              {Object.entries(groupMedicationsByDate(prescriptionMedications)).map(([date, medications]) => (
                                <Box key={date}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                                    {date}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                                    {medications.map((medication, index) => {
                                      const currentStatus = getMedicationStatus(prescription.id, medication.id, medication.status);
                                      const serverKey = `${prescription.id}-${medication.id}`;
                                    
                                      const isLoadingAction = medicationActionLoading.has(serverKey);
                                      
                                      return (
                                        <Paper key={medication.id} variant="outlined" sx={{ p: 1.5, bgcolor: 'background.paper', position: 'relative' }}>
                                          {isLoadingAction && (
                                            <Box sx={{ 
                                              position: 'absolute', 
                                              top: 0, 
                                              left: 0, 
                                              right: 0, 
                                              bottom: 0, 
                                              bgcolor: 'rgba(255,255,255,0.7)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              zIndex: 1,
                                              borderRadius: 1
                                            }}>
                                              <CircularProgress size={20} />
                                            </Box>
                                          )}
                                          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                            {/* Tooltip gives the reason BEFORE the click, rather
                                                than a 409 after it. */}
                                            <Tooltip
                                              title={isBlockedRow(medication)
                                                ? 'Payment has not been recorded for this item. It cannot be dispensed yet.'
                                                : ''}
                                              disableHoverListener={!isBlockedRow(medication)}
                                            >
                                            <FormControlLabel
                                            control={
                                              <Checkbox
                                              checked={currentStatus === 'served'}
                                              onChange={(e) => handleMedicationStatusChange(prescription.id, medication.id, e.target.checked)}
                                              disabled={
                                                medication.status === 'completed' ||
                                                medication.status === 'served' ||
                                                medication.status === 'not_served' ||
                                                medication.status?.toLowerCase() === 'unknown' ||
                                                currentStatus === 'served' ||
                                                currentStatus === 'not_served' ||
                                                currentStatus?.toLowerCase() === 'unknown' ||
                                                isLoadingAction ||
                                                // Unpaid and the facility does not allow dispensing
                                                // it. The server refuses this too - the disabled
                                                // control is the courtesy, not the enforcement.
                                                isBlockedRow(medication)
}
                                              size="small"
                                              sx={getCheckboxStyles(medication.status, currentStatus)}
                                            />
                                            }
                                            label=""
                                            sx={{ m: 0, minWidth: 'auto' }}
                                          />
                                            </Tooltip>
                                            <Box sx={{ 
                                              display: 'flex', 
                                              alignItems: 'center', 
                                              justifyContent: 'center',
                                              width: 24,
                                              height: 24,
                                              bgcolor: 'primary.light',
                                              color: 'primary.contrastText',
                                              borderRadius: '50%',
                                              fontWeight: 600,
                                              flexShrink: 0,
                                              fontSize: '0.75rem'
                                            }}>
                                              {index + 1}
                                            </Box>
                                            <Box sx={{ flexGrow: 1 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5, flexWrap: 'wrap' }}>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                  {medication.name}
                                                </Typography>
                                                {/* Unpaid badge, right next to the drug name.
                                                    Two different wordings on purpose: they are
                                                    two different instructions to the reader. A
                                                    row that looks identical to a paid one is
                                                    worse than not showing it, because the
                                                    pharmacist will dispense it. */}
                                                {isUnpaidRow(medication) && (
                                                  <Chip
                                                    size="small"
                                                    label={unpaidPolicy.canServeUnpaid
                                                      ? 'UNPAID · may dispense'
                                                      : 'UNPAID · payment required'}
                                                    sx={{
                                                      height: 20,
                                                      fontSize: '0.65rem',
                                                      fontWeight: 700,
                                                      bgcolor: unpaidPolicy.canServeUnpaid
                                                        ? 'rgba(217,119,6,0.14)'
                                                        : 'rgba(220,38,38,0.12)',
                                                      color: unpaidPolicy.canServeUnpaid
                                                        ? '#A16207'
                                                        : '#991B1B',
                                                    }}
                                                  />
                                                )}
                                              </Box>
                                              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                                                {medication.dosage} {medication.route} ,  {medication.frequency} for {medication.duration}  {medication.duration >1  ? 'Days' :'Day'}
                                              </Typography>
                                              <Typography variant="caption" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                                                {medication.instructions}
                                              </Typography>
                                              
                                              {/* Server Information */}
                                              {medication.doc_name  && (
                                            <Box sx={{ 
                                              mt: 1, 
                                              p: 1.5, 
                                              bgcolor: 'rgba(46, 125, 50, 0.1)', 
                                              borderRadius: 2,
                                              borderLeft: '4px solid',
                                              borderLeftColor: 'success.main'
                                            }}>
                                                <Typography variant="body2" sx={{ 
                                                fontWeight: 600, 
                                                color: currentStatus === 'not_served' ? 'error.dark' : 'success.dark',
                                                mb: 0.5
                                              }}>
                                                Prescribed: {medication.doc_name}
                                                {currentStatus === 'served' && ` • ✓ Served: ${medication.user}`}
                                                {(currentStatus === 'not_served' || medication.status === 'not_served') && 
                                                  ` • ✗ Not Served: ${medication.user || medication.doc_name}`
                                                }
                                              </Typography>
                                              <Typography variant="caption" sx={{ 
                                                color: 'text.secondary', 
                                                fontSize: '0.7rem',
                                                fontStyle: 'italic'
                                              }}>
                                                {medication.time}
                                              </Typography>
                                            </Box>
                                          )}
                                            </Box>
                                            <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                                              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                Qty: {medication.quantity || 'N/A'}
                                              </Typography>
                                              <Chip
                                                label={currentStatus}
                                                color={getStatusColor(currentStatus)}
                                                size="small"
                                              />
                                              {/* ★ NEW — Not Served button, only for paid/pending meds */}
                                              {currentStatus !== 'served' && currentStatus !== 'not_served' && currentStatus !== 'completed' && (
                                                <Button
                                                  size="small"
                                                  color="error"
                                                  variant="outlined"
                                                  sx={{ mt: 0.75, fontSize: "0.65rem", py: 0.25, px: 1, display: "block", minWidth: 0 }}
                                                  onClick={() => {
                                                    setSelectedMedForNotServed({ ...medication, patientName: prescription.patientName });
                                                    setNotServedDialog(true);
                                                  }}
                                                >
                                                  Not Served
                                                </Button>
                                              )}
                                              {currentStatus === 'not_served' && (
                                                <Chip label="Not Served" color="error" size="small" sx={{ mt: 0.5, fontSize: "0.65rem" }} />
                                              )}
                                            </Box>
                                          </Box>
                                        </Paper>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              ))}
                            </Box>

                            {/* Notes */}
                            {prescription.notes && (
                              <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: 'info.light', color: 'info.contrastText' }}>
                                <Typography variant="caption">
                                  <strong>Notes:</strong> {prescription.notes}
                                </Typography>
                              </Paper>
                            )}
                          </Paper>
                        </Collapse>
                      )}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 0.75, flexDirection: { xs: 'column', sm: 'row' } }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<Visibility sx={{ fontSize: 16 }} />}
                        onClick={() => handleOpenPreviewDialog(prescription)}
                        sx={{
                          flex: 1, textTransform: 'none', fontWeight: 700,
                          bgcolor: PRIMARY, boxShadow: 'none',
                          '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' },
                        }}
                      >
                        Dispense
                      </Button>
                      <LoadingButton
                        loading={vitalLoading && selectedPatient?.id === prescription.id}
                        variant="outlined"
                        size="small"
                        startIcon={<MonitorHeart sx={{ fontSize: 16 }} />}
                        onClick={() => handleOpenVitalDialog(prescription)}
                        sx={{
                          flex: 1, textTransform: 'none', fontWeight: 700,
                          borderColor: BORDER, color: MUTED,
                          '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
                        }}
                      >
                        Vitals
                      </LoadingButton>
                      <LoadingButton
                        loading={vitalLoading && selectedPatient?.id === prescription.id}
                        variant="outlined"
                        size="small"
                        startIcon={<MedicationLiquidIcon sx={{ fontSize: 16 }} />}
                        onClick={() => openPatientMed(prescription)}
                        sx={{
                          flex: 1, textTransform: 'none', fontWeight: 700,
                          borderColor: BORDER, color: MUTED,
                          '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
                        }}
                      >
                        History
                      </LoadingButton>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Fade>
      )}
 
      {/* No Results State */}
      {!initialLoading && !searchLoading && filteredPrescriptions.length === 0 && (
        <Fade in={true}>
          <Paper elevation={0} sx={{
            p: 5, textAlign: 'center',
            borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
          }}>
            <LocalHospital sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG, mb: 0.5 }}>
              No prescriptions found
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: MUTED, mb: 2 }}>
              {prescriptions.length === 0
                ? 'No prescriptions available right now.'
                : prescriptions.filter(p => getMedicationCounts(p.id).total > 0).length === 0
                  ? 'No prescriptions with medications available.'
                  : 'Try adjusting your search or filter.'}
            </Typography>
            {searchQuery && (
              <Button
                variant="outlined" size="small"
                onClick={() => setSearchQuery('')}
                startIcon={<Clear sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: 'none', fontWeight: 700,
                  borderColor: BORDER, color: MUTED,
                  '&:hover': { borderColor: PRIMARY, color: PRIMARY, bgcolor: PRIMARY_BG },
                }}
              >
                Clear search
              </Button>
            )}
          </Paper>
        </Fade>
      )}

      {/* NEW: Prescription Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        // Route backdrop / ESC closes through handleClosePreviewDialog so
        // the soft-block prompt fires whenever the pharmacist tries to
        // bail with medications still pending — not just on the X button.
        onClose={(_e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleClosePreviewDialog();
            return;
          }
          handleClosePreviewDialog();
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '90vh',
            maxHeight: '95vh',
            width: '95vw',
            maxWidth: '1400px'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: PRIMARY, color: '#fff',
          py: 1.75, px: 2.5,
          position: 'sticky', top: 0, zIndex: 1,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Visibility sx={{ fontSize: 22 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }} noWrap>
                Prescription preview · {previewPrescription?.id}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }} noWrap>
                {previewPrescription?.patientName?.toUpperCase()}
                {previewPrescription?.patientType ? ` · ${previewPrescription.patientType}` : ''}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClosePreviewDialog}
            sx={{ color: '#fff' }}
            size="small"
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        
          <DialogContent sx={{ p: 0, height: 'calc(90vh - 120px)', overflow: 'hidden' }}>
          {previewPrescription && (
            <Box sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              bgcolor: '#fafafa'
            }}>
              {/* Medication Summary Header */}
              <Box sx={{ 
                p: 3, 
                bgcolor: 'white', 
                borderBottom: '2px solid #e0e0e0',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    Medication Summary
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge badgeContent={getMedicationCounts(previewPrescription.id).total} color="primary" max={99}>
                        <Chip label="Total" variant="outlined" />
                      </Badge>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge badgeContent={getMedicationCounts(previewPrescription.id).served} color="success" max={99}>
                        <Chip label="Served" color="success" variant="outlined" />
                      </Badge>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Badge badgeContent={getMedicationCounts(previewPrescription.id).pending} color="warning" max={99}>
                        <Chip label="Pending" color="warning" variant="outlined" />
                      </Badge>
                    </Box>
                  </Box>
                </Box>

                {/*  ── WHAT IS BEING TREATED, WHERE THE SERVING HAPPENS ───────
                     The card outside carries this, but the pharmacist does the
                     actual work in here — ticking Serve on each line — and this
                     dialog covers the card completely. Without it they read the
                     diagnosis, open the preview, and it is gone.

                     Directly under the Medication Summary heading, above the
                     drug table, so it is read before anything is ticked rather
                     than after.

                     Same rule as the card: each of the two carries ITS OWN date,
                     shown whenever it is not today. On this ward 6 of the 8
                     patients with both have them recorded on different days,
                     some years apart — one shared label would misdate them. */}
                {(previewPrescription.diagnoses || previewPrescription.procedures) && (
                  <Box sx={{
                    display: 'flex', gap: 1, alignItems: 'flex-start',
                    px: 2, py: 1.25, borderRadius: 1.5,
                    bgcolor: 'rgba(234,179,8,0.08)',
                    border: '1px solid rgba(234,179,8,0.28)',
                  }}>
                    <LocalHospital sx={{ fontSize: 18, color: '#a16207', mt: '2px', flexShrink: 0 }} />
                    <Box sx={{
                      display: 'flex', flexWrap: 'wrap', gap: 3, minWidth: 0, flex: 1,
                    }}>
                      {[
                        ['Diagnosis', previewPrescription.diagnoses, previewPrescription.diagnosis_date],
                        ['Procedure', previewPrescription.procedures, previewPrescription.procedure_date],
                      ].filter(([, text]) => text).map(([label, text, when]) => {
                        const d = String(when || '').slice(0, 10);
                        const today = new Date().toISOString().slice(0, 10);
                        const stamp = d && d !== today ? ` · ${d}` : '';
                        return (
                          <Box key={label} sx={{ minWidth: 180 }}>
                            <Typography sx={{
                              fontSize: '0.68rem', fontWeight: 800, color: '#a16207',
                              letterSpacing: '0.05em', textTransform: 'uppercase',
                              lineHeight: 1.2,
                            }}>
                              {label}{stamp}
                            </Typography>
                            <Typography sx={{
                              fontSize: '0.92rem', color: '#1e293b',
                              lineHeight: 1.4, mt: '2px',
                            }}>
                              {text}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}

              </Box>

               {/* Medications Section with Serve All */}
               <Box sx={{ 
                 flexGrow: 1, 
                 display: 'flex', 
                 flexDirection: 'column',
                 overflow: 'hidden'
               }}>
                 <Box sx={{ 
                   p: 3, 
                   bgcolor: 'white', 
                   borderBottom: '1px solid #e0e0e0',
                   display: 'flex',
                   justifyContent: 'space-between',
                   alignItems: 'center',
                   flexShrink: 0
                 }}>
                   <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                     Medications ({getMedicationsForPrescription(previewPrescription.id).length})
                   </Typography>
                   
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                     <FormControlLabel
                       control={
                         <Checkbox
                           checked={getServeAllStatus(previewPrescription.id)}
                           onChange={(e) => handleServeAllChange(previewPrescription.id, e.target.checked)}
                           color="primary"
                           sx={{ transform: 'scale(1.2)' }}
                         />
                       }
                       label={
                         <Typography variant="h6" sx={{ fontWeight: 400, color: 'primary.main' }}>
                           Serve All Medications
                         </Typography>
                       }
                     />
                   </Box>
                 </Box>

                {/* Medications Table */}
                 <Box sx={{ 
                   flexGrow: 1, 
                   overflow: 'auto', 
                   p: 3,
                   bgcolor: '#fafafa'
                 }}>
                  <Paper elevation={2} sx={{ overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                    <Box sx={{ 
                      overflow: 'auto',
                      maxHeight: 'calc(90vh - 300px)',
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px'
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a1a1a1',
                      }
                    }}>
                      <table style={{ 
                        width: '100%', 
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}>
                        <thead>
                          <tr style={{ 
                            backgroundColor: PRIMARY,
                            color: 'white',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1
                          }}>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '60px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Serve
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '50px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              #
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'left', 
                              fontWeight: 600,
                              minWidth: '200px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Medication Name
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '120px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Dosage & Route
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '100px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Frequency
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '80px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Duration
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '80px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Quantity
                            </th>
                            
                            
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '100px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Date
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'center', 
                              fontWeight: 600,
                              minWidth: '100px',
                              borderRight: '1px solid rgba(255,255,255,0.2)'
                            }}>
                              Status
                            </th>
                            <th style={{ 
                              padding: '16px 12px', 
                              textAlign: 'left', 
                              fontWeight: 600,
                              minWidth: '180px'
                            }}>
                              Prescribed/Served By
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getMedicationsForPrescription(previewPrescription.id).map((medication, index) => {
                            const currentStatus = getMedicationStatus(previewPrescription.id, medication.id, medication.status);
                            const serverKey = `${previewPrescription.id}-${medication.id}`;
                            const isLoadingAction = medicationActionLoading.has(serverKey);
                            const isServed = currentStatus === 'served';
                            
                            return (
                              <tr 
                                key={medication.id}
                                style={{ 
                                  backgroundColor: isServed ? '#f8fff8' : index % 2 === 0 ? '#fafafa' : 'white',
                                  borderLeft: isServed ? '4px solid #4caf50' : '4px solid transparent',
                                  position: 'relative'
                                }}
                              >
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0',
                                  position: 'relative'
                                }}>
                                  {isLoadingAction && (
                                    <Box sx={{ 
                                      position: 'absolute', 
                                      top: '50%', 
                                      left: '50%', 
                                      transform: 'translate(-50%, -50%)',
                                      zIndex: 1
                                    }}>
                                      <CircularProgress size={16} />
                                    </Box>
                                  )}
                                  <Checkbox
                                    
                                    onChange={(e) => handleMedicationStatusChange(previewPrescription.id, medication.id, e.target.checked)}
                                    disabled={
                                      medication.status === 'completed' || 
                                      medication.status === 'served' ||
                                      medication.status === 'not_served' ||
                                      medication.status?.toLowerCase() === 'unknown' ||
                                      currentStatus === 'not_served' ||
                                      currentStatus?.toLowerCase() === 'unknown' ||
                                      isLoadingAction ||
                                      // Same payment gate as the card view.
                                      isBlockedRow(medication)
                                    }
                                    size="small"
                                    sx={{
                                      ...getCheckboxStyles(medication.status, currentStatus),
                                      opacity: isLoadingAction ? 0.3 : 1
                                    }}
                                  />
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0',
                                  fontWeight: 600,
                                  color: PRIMARY_DARK
                                }}>
                                  {index + 1}
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0',
                                  fontWeight: 600,
                                  color: '#1e293b'
                                }}>
                                  {medication.name}
                                  {isUnpaidRow(medication) && (
                                    <Chip
                                      size="small"
                                      label={unpaidPolicy.canServeUnpaid
                                        ? 'UNPAID · may dispense'
                                        : 'UNPAID · payment required'}
                                      sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        bgcolor: unpaidPolicy.canServeUnpaid
                                          ? 'rgba(217,119,6,0.14)'
                                          : 'rgba(220,38,38,0.12)',
                                        color: unpaidPolicy.canServeUnpaid
                                          ? '#A16207'
                                          : '#991B1B',
                                      }}
                                    />
                                  )}
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0'
                                }}>
                                  {medication.dosage} {medication.route}
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0'
                                }}>
                                  {medication.frequency}
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0'
                                }}>
                                  {medication.duration} {medication.duration > 1 ? 'Days' : 'Day'}
                                </td>
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0',
                                  fontWeight: 600
                                }}>
                                  {medication.quantity || 'N/A'}
                                </td>
                                
                                <td style={{ 
                                  padding: '12px', 
                                  textAlign: 'center',
                                  borderBottom: '1px solid #e0e0e0',
                                  borderRight: '1px solid #e0e0e0',
                                  fontSize: '13px'
                                }}>
                                  {medication.administrationDate || 'N/A'}
                                </td>

                                <td style={{
                                    padding: '12px', textAlign: 'center',
                                    borderBottom: '1px solid #e0e0e0',
                                    borderRight: '1px solid #e0e0e0'
                                  }}>
                                    <Chip
                                      label={currentStatus.toUpperCase()}
                                      color={getStatusColor(currentStatus)}
                                      size="small"
                                      sx={{ fontWeight: 600, fontSize: '11px', height: 24 }}
                                    />
                                    {/* ★ NEW */}
                                    {currentStatus !== 'served' && currentStatus !== 'not_served' && currentStatus !== 'completed' && (
                                      <Button
                                        size="small" color="error" variant="outlined"
                                        sx={{ mt: 0.5, fontSize: "0.65rem", py: 0.25, px: 1, display: "block", mx: "auto", minWidth: 0 }}
                                        onClick={() => {
                                          setSelectedMedForNotServed({ ...medication, patientName: previewPrescription.patientName });
                                          setNotServedDialog(true);
                                        }}
                                      >
                                        Not Served
                                      </Button>
                                    )}
                                    {currentStatus === 'not_served' && (
                                      <Chip label="Not Served" color="error" size="small" sx={{ mt: 0.5, fontSize: "0.65rem" }} />
                                    )}
                                  </td>
                                <td style={{ 
                                  padding: '12px', 
                                  borderBottom: '1px solid #e0e0e0',
                                  fontSize: '13px',
                                  lineHeight: '1.4'
                                }}>
                                  {medication.doc_name && (
                                    <Box>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2', mb: 0.5 }}>
                                        Prescribed: {medication.doc_name}
                                      </Typography>

                                      {/* ── Served ── */}
                                      {medication.user && currentStatus === 'served' && (
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                          ✓ Served: {medication.user}
                                        </Typography>
                                      )}

                                      {/* ── Not Served ── */}
                                      {(currentStatus === 'not_served' || medication.status === 'not_served') && (
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                                          ✗ Not Served: {medication.user || medication.doc_name}
                                        </Typography>
                                      )}

                                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5 }}>
                                        {medication.time}
                                      </Typography>
                                    </Box>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>
                  </Paper>
                   
                   {/* Notes */}
                   {previewPrescription.notes && (
                    <Paper elevation={2} sx={{ mt: 3, p: 3, border: '2px solid #2196f3' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                          Additional Notes
                        </Typography>
                        <Typography variant="body1">
                          {previewPrescription.notes}
                        </Typography>
                    </Paper>
                   )}
                 </Box>
               </Box>
            </Box>
          )}
        </DialogContent>
        

        <DialogActions sx={{ 
          p: 3, 
          bgcolor: '#f5f5f5', 
          borderTop: '1px solid #e0e0e0',
          position: 'sticky',
          bottom: 0,
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary">
            Last updated: {new Date().toLocaleString()}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
           
            <Button 
              onClick={handleClosePreviewDialog} 
              variant="contained" 
              size="large"
            >
              Close Preview
            </Button>
          </Box>
        </DialogActions>
      </Dialog>



    {/* Vital Signs and Clinical Notes Dialog - Separated Sections */}
      <Dialog
        open={vitalDialogOpen}
        // Route every close path (button click, backdrop, ESC) through
        // handleCloseVitalDialog so the soft-block warning fires when
        // there are pending medications.
        onClose={(_e, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleCloseVitalDialog();
            return;
          }
          handleCloseVitalDialog();
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '85vh', maxHeight: '90vh' }
        }}
      >
        <DialogTitle sx={{
          bgcolor: PRIMARY, color: '#fff',
          py: 1.75, px: 2.5,
          position: 'relative',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <MonitorHeart sx={{ fontSize: 22 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {selectedPatient?.patientName} ({selectedPatient?.id})
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', opacity: 0.9 }}>
              Vital signs &amp; clinical notes
            </Typography>
          </Box>
          <IconButton onClick={handleCloseVitalDialog} sx={{ color: '#fff' }} size="small">
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, height: 'calc(85vh - 120px)', display: 'flex', flexDirection: 'column' }}>
          {selectedPatient && getPatientVitals(selectedPatient.id) && (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {/* Vital Signs Section - Compact and Separate */}
              <Box sx={{ p: 3, bgcolor: '#fafafa', flexShrink: 0, borderBottom: '2px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Favorite  />
                  Current Vital Signs
                </Typography>
                
                <Grid container spacing={2} columns={{ xs: 4, sm: 8, md: 12 }} >
                  <Grid item xs={6} sm={4} md={3} size={2}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <Thermostat sx={{ fontSize: 24, color: '#ff9800', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                        Temperature
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPatientVitals(selectedPatient.id).temperature}
                      </Typography>
                    </Paper>
                  </Grid>
                  
                  <Grid item xs={6} sm={4} md={3} size={2}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <BloodtypeOutlined sx={{ fontSize: 24, color: '#d32f2f', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                        Blood Pressure
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPatientVitals(selectedPatient.id).bloodPressure}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3} size={2} >
                    <Paper sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <Favorite sx={{ fontSize: 24, color: '#e91e63', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                        Pulse
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPatientVitals(selectedPatient.id).heartRate}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3} size={2}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <MonitorHeart sx={{ fontSize: 24, color: '#2196f3', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                        Respiratory Rate
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPatientVitals(selectedPatient.id).respiratoryRate}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3} size={2}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <Scale sx={{ fontSize: 24, color: '#9c27b0', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                        Weight
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPatientVitals(selectedPatient.id).weight}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6} sm={4} md={3} size={2}>
                    <Paper sx={{ p: 1.5, textAlign: 'center', border: '1px solid #e0e0e0' }}>
                      <Height sx={{ fontSize: 24, color: '#607d8b', mb: 0.5 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600 }}>
                        Height
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getPatientVitals(selectedPatient.id).height}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Last Updated: {getPatientVitals(selectedPatient.id).lastUpdated}
                  </Typography>
                </Box>
              </Box>

              {/* Clinical Notes Section - Completely Separate */}
              <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: '#ffffff' }}>
                <Box sx={{ p: 3, pb: 2, flexShrink: 0, borderBottom: '1px solid #e0e0e0' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: '#388823ff' }}>
                    <NoteAlt sx={{ color: '#137c24ff' }} />
                    Clinical Notes
                  </Typography>
                </Box>

                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'auto', 
                  px: 3, 
                  pb: 3,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#c1c1c1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#a1a1a1',
                  },
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    {getPatientClinicalNotes(selectedPatient.id).map((note, index) => (
                      <Paper key={index} elevation={3} sx={{ overflow: 'hidden', border: '1px solid #e0e0e0' }}>
                        {/* Note Header */}
                        <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderBottom: '2px solid #2196f3' }}>
                         
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                           <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                             Last Updated: {note.time}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            For complete clinical notes on this patient, please contact the attending doctor
                            </Typography>

                            <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                              by: {note.author}
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ p: 3 }}>
                          {/* Chief Complaint */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: '#fff5f5', borderLeft: '4px solid #f44336', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#d32f2f', mb: 1 }}>
                              Patient Complaint
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                              {note.chiefComplaint}
                            </Typography>
                          </Box>

                          {/* History */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: '#f3f8ff', borderLeft: '4px solid #2196f3', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
                              Patient History
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                              {note.hpi}
                            </Typography>
                          </Box>

                          {/* Past Medical History */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fff8', borderLeft: '4px solid #4caf50', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#388e3c', mb: 1 }}>
                              Physical Examination Results
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                              {note.pastMedicalHistory}
                            </Typography>
                          </Box>

                          {/* Examination */}
                          <Box sx={{ mb: 3, p: 2, bgcolor: '#fffbf0', borderLeft: '4px solid #ff9800', borderRadius: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f57c00', mb: 1 }}>
                              Diagnosis(es)
                            </Typography>
                            <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                              {note.documenting}
                            </Typography>
                          </Box>

                          {/* Procedure */}
                          <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderLeft: '4px solid #4caf50', borderRadius: 1, border: '2px solid #c8e6c9' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1 }}>
                              Procedure 
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500, lineHeight: 1.6 }}>
                              {note.Procedure_}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}

                    {/* ── LABORATORY RESULTS ────────────────────────────────
                        Grouped into panels by the server: one card per test,
                        per lab number, per day. A Full Blood Count is twenty
                        parameter rows in the table and one result to a
                        pharmacist, and the table it is read from is the same
                        one the laboratory screen writes.

                        `value` is the `comments` column and `flag` is the flag
                        — the aliasing claimLabPanel.js uses. Two screens
                        disagreeing about which column holds a haemoglobin is
                        not something anyone would notice until it mattered. */}
                    {labHistory.labs.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{
                          fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1,
                          color: '#6a1b9a', mb: 1.5,
                        }}>
                          <ScienceIcon sx={{ color: '#6a1b9a' }} />
                          Laboratory results
                          <Chip size="small" label={labHistory.labPanelsTotal > labHistory.labs.length
                            ? `${labHistory.labs.length} of ${labHistory.labPanelsTotal}`
                            : `${labHistory.labs.length}`}
                            sx={{ bgcolor: '#f3e5f5', color: '#6a1b9a', fontWeight: 700 }} />
                        </Typography>

                        {labHistory.labs.map((panel, pi) => (
                          <Paper key={`lab-${panel.dat}-${panel.path_no}-${pi}`} elevation={0}
                            sx={{ mb: 1.5, border: '1px solid #e1bee7', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{
                              px: 2, py: 1.25, bgcolor: '#faf5fc',
                              borderBottom: '1px solid #e1bee7',
                              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                            }}>
                              <Typography sx={{ fontWeight: 700, color: '#4a148c', flex: 1, minWidth: 160 }}>
                                {panel.test}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#6a1b9a', fontWeight: 600 }}>
                                {panel.dat}
                              </Typography>
                              {panel.path_no && (
                                <Chip size="small" label={panel.path_no}
                                  sx={{ fontFamily: 'monospace', fontSize: '0.68rem', height: 20 }} />
                              )}
                            </Box>

                            <TableContainer>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#fcfafd' }}>Parameter</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#fcfafd' }}>Result</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#fcfafd' }}>Unit</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', bgcolor: '#fcfafd' }}>Reference</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {panel.parameters.map((p, i) => (
                                    <TableRow key={`${p.parameter}-${i}`} hover>
                                      <TableCell sx={{ fontSize: '0.8rem' }}>{p.parameter}</TableCell>
                                      <TableCell sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
                                        {p.value || '—'}
                                        {/*  The flag only when it says something the
                                            value does not already. On this data the
                                            two are usually identical strings, and
                                            printing both reads as two results.  */}
                                        {p.flag && p.flag.toLowerCase() !== String(p.value).toLowerCase() && (
                                          <Chip size="small" label={p.flag}
                                            sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
                                        )}
                                      </TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem', color: '#666' }}>{p.unit || '—'}</TableCell>
                                      <TableCell sx={{ fontSize: '0.75rem', color: '#666' }}>{p.ref_range || '—'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>

                            {panel.impression && (
                              <Box sx={{ px: 2, py: 1.25, bgcolor: '#f9f9f9', borderTop: '1px solid #eee' }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#6a1b9a' }}>
                                  Impression
                                </Typography>
                                <Typography variant="body2">{panel.impression}</Typography>
                              </Box>
                            )}
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/* ── SCANS ─────────────────────────────────────────────
                        Findings are prose, not numbers, so this is a list of
                        paragraphs rather than a table. scan_result keys the
                        patient on `patient_id` where the lab tables use `id`;
                        the endpoint absorbs that difference. */}
                    {labHistory.scans.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{
                          fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1,
                          color: '#00695c', mb: 1.5,
                        }}>
                          <MonitorHeart sx={{ color: '#00695c' }} />
                          Scans
                          <Chip size="small" label={labHistory.scansTotal > labHistory.scans.length
                            ? `${labHistory.scans.length} of ${labHistory.scansTotal}`
                            : `${labHistory.scans.length}`}
                            sx={{ bgcolor: '#e0f2f1', color: '#00695c', fontWeight: 700 }} />
                        </Typography>

                        {labHistory.scans.map((s, si) => (
                          <Paper key={`scan-${s.dat}-${s.scan}-${si}`} elevation={0}
                            sx={{ mb: 1.5, border: '1px solid #b2dfdb', borderRadius: 2, overflow: 'hidden' }}>
                            <Box sx={{
                              px: 2, py: 1.25, bgcolor: '#f2fbfa',
                              borderBottom: '1px solid #b2dfdb',
                              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
                            }}>
                              <Typography sx={{ fontWeight: 700, color: '#004d40', flex: 1, minWidth: 160 }}>
                                {s.scan}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#00695c', fontWeight: 600 }}>
                                {s.dat}
                              </Typography>
                            </Box>
                            <Box sx={{ p: 2 }}>
                              {s.findings.map((f, i) => (
                                <Box key={`${f.parameter}-${i}`} sx={{ mb: 1.25 }}>
                                  {f.parameter && (
                                    <Typography variant="caption"
                                      sx={{ fontWeight: 700, color: '#00695c', display: 'block' }}>
                                      {f.parameter}
                                    </Typography>
                                  )}
                                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                    {f.finding || '—'}
                                  </Typography>
                                </Box>
                              ))}
                              {s.impression && (
                                <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#00695c' }}>
                                    Impression
                                  </Typography>
                                  <Typography variant="body2">{s.impression}</Typography>
                                </Box>
                              )}
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}

                    {/*  Said, rather than shown as blank space. "No results" and
                        "results not loaded" look identical otherwise, and a
                        pharmacist reading an empty panel as "nothing came back"
                        when the request failed is the wrong conclusion. */}
                    {!vitalLoading && labHistory.labs.length === 0
                      && labHistory.scans.length === 0 && (
                      <Box sx={{
                        p: 2, mb: 2, borderRadius: 2, bgcolor: '#f8f9fa',
                        border: '1px dashed #cfd8dc', textAlign: 'center',
                      }}>
                        <Typography variant="body2" color="text.secondary">
                          No laboratory results or scans are recorded for this patient.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, bgcolor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={handleCloseVitalDialog} variant="contained" color="primary" size="large">
            Close
          </Button>
        </DialogActions>
      </Dialog>


      
  <RestockNotificationDialog
  onSeen={() => setRestockCount(0)}
  onNotificationsLoaded={(count) => setRestockCount(count)}
/>


  {/* Medication History */}
    <MedicationHistory
        open={openmedHis}
        onClose={closePatientMed}
        id={selectedPatientt ? selectedPatientt : ''}
       // onSubmit={getCCCData}
     />

     <NotServedDialog
        open={notServedDialog}
        onClose={() => {
          setNotServedDialog(false);
          setSelectedMedForNotServed(null);
        }}
        medication={selectedMedForNotServed}
        onConfirm={handleNotServedConfirm}
        loading={notServedLoading}
      />

      <ReversalDialog
        open={notServedReversalDialog}
        onClose={() => {
          setNotServedReversalDialog(false);
          setNotServedReversalData(null);
        }}
        transactionData={notServedReversalData}
        user={currentUser}
      />

      {/* Pharmacy attention reminder — surfaces every patient this user
          opened but didn't fully disposition. Fetched on mount and on
          every successful serve/not-served action via refreshKey. */}
      <PharmacyAttentionDialog
        pharmacist={currentUser}
        refreshKey={attentionRefreshKey}
        onResolvePatient={(row) => {
          // Try to match the real prescription row that's already on the
          // page (so the preview dialog gets the correct medications list
          // and patient metadata). Fall back to a minimal stub if the
          // patient is no longer in the current view.
          const match = (prescriptions || []).find(p =>
            String(p.id) === String(row.patient_id)
          );
          const target = match || {
            id:           row.patient_id,
            patientName:  row.patient_name,
            patientId:    row.patient_id,
            patient_name: row.patient_name,
            patient_tel:  row.patient_tel,
            dat:          row.visit_date,
            visitDate:    row.visit_date,
          };
          // Open the Dispense (preview) dialog — that's where the
          // pharmacist actually serves / marks not-served.
          handleOpenPreviewDialog(target);
        }}
      />

    </Box>
  );
}


